<template>
    <div class="WorkOrderForm">
        <DashboardEditSection title="Information">
            <FormSelectClient
                v-model.number="$v.client.$model"
                :errors="$v.client.$error ? [
                    !$v.client.required && 'Client is required',
                ] : null"
                showCreateOption
                required
            />
            <FormInput
                v-model="$v.name.$model"
                lazy
                label="Work Order Name"
                :errors="$v.name.$error ? [
                    !$v.name.required && 'Work Order name is required',
                ] : null"
                required
            />
            <AddressForm
                :address.sync="address"
                :city.sync="city"
                :state.sync="state"
                :zip.sync="zip"
            />

            <FormTextarea v-model="notes" label="Notes"/>

            <div>
                <Banner variant="warning" isSlim>
                    WARNING: These fields are for the Haul Sheet Header. You can now manage multiple sites separately. <router-link to="/company/sites" class="link primary">Create site</router-link>
                </Banner>
                <Row>
                    <Column :md="6">
                        <FormInput v-model="loadSite" label="Load Site Name"/>
                    </Column>
                    <Column :md="6">
                        <FormInput v-model="dumpSite" label="Dump Site Name"/>
                    </Column>
                </Row>
            </div>
        </DashboardEditSection>

        <DashboardEditSection title="Sites" :requiresUpgrade="isFreePlan">
            <Banner variant="info" icon="info-circle" isSlim>
                Available on DumpTek mobile app version 0.4.5 or later. Update the app to use this feature.
            </Banner>
            <div>
                <div class="toggleRow">
                    <h4>Enable sites</h4>
                    <p>When enabled, drivers can select <u>Load Site</u> and <u>Dump Site</u> from your company’s <router-link to="/company/sites">Site list</router-link> for each ticket load in the DumpTek mobile app. You can further restrict which Sites are available by using the work order-level limit option.</p>
                    <FormToggle
                        v-model="isSitesEnabled"
                        @input="handleToggleSitesEnabled"
                    />
                </div>
            </div>

            <div v-show="isSitesEnabled">
                <div class="toggleRow">
                    <h4>Limit sites</h4>
                    <p>By default, <u>all sites</u> are available in the DumpTek mobile app. To <u>limit sites</u> for this work order, enable this option and select the sites drivers can use.</p>
                    <FormToggle
                    v-model="isSitesRestricted"
                    @input="handleToggleRestrictSites"
                    />
                </div>
                <WorkOrderSiteSelector
                    v-show="isSitesRestricted"
                    ref="workOrderSiteSelector"
                    :sites.sync="sites"
                />
            </div>
        </DashboardEditSection>

        <DashboardEditSection title="Pricing" class="pricingSection" :requiresUpgrade="isFreePlan">
            <FormSelect v-model="rateType" label="Rate Type" class="rateTypeSelector">
                <option value="hourly">Hourly</option>
                <option value="material">By Material</option>
            </FormSelect>

            <div v-if="rateType === 'hourly'" class="pricingType">
                <Row>
                    <Column :md="4">
                        <FormCurrency v-model="hourlyRate" label="Hourly Rate" :min="0"/>
                    </Column>
                </Row>
                <WorkOrderHourlyRateOptions :hourlyRateOptions.sync="hourlyRateOptions"/>
            </div>
            <div v-else class="pricingType">
                <Banner variant="info" icon="info-circle" isSlim>
                    Rates are set per material in the Materials section below.
                </Banner>
                <WorkOrderHourlyRateOptions :hourlyRateOptions.sync="hourlyRateOptions" isMaterial/>
            </div>

            <!-- <h2>Mobilization &amp; Demobilization</h2>
            <p>Disclaimer: Mobilization and Demobilization prices represent the total price for the entire Work Order.</p>
            <Row>
                <Column :md="6">
                    <FormCurrency v-model="mobilizationPrice" label="Mobilization Price" :min="0"/>
                </Column>
                <Column :md="6">
                    <FormCurrency v-model="demobilizationPrice" label="Demobilization Price" :min="0"/>
                </Column>
            </Row> -->
        </DashboardEditSection>

        <DashboardEditSection title="Materials" :requiresUpgrade="isFreePlan">
            <p class="sectionHint">List the materials for this work order and how much of each is expected. When the rate type is <u>By Material</u>, each material's rate is set here too.</p>
            <WorkOrderMaterialList
                :materialRates.sync="materialRates"
                :materials.sync="materials"
                :rateType="rateType"
            />
        </DashboardEditSection>

        <DashboardEditSection title="Reporting" :requiresUpgrade="isFreePlan">
            <div class="toggleRow">
                <h4>Time overrides</h4>
                <FormToggle
                    v-model="isTimeOverridesEnabled"
                    @input="handleToggleTimeOverrides"
                />
            </div>
            <WorkOrderReportTimeOverrides
                v-show="isTimeOverridesEnabled"
                ref="workOrderReportTimeOverrides"
                :reportTimeOverrides.sync="reportTimeOverrides"
            />
        </DashboardEditSection>

        <DashboardEditSection title="Contact">
            <Row>
                <Column :md="6">
                    <FormInput v-model="firstName" label="First Name"/>
                </Column>
                <Column :md="6">
                    <FormInput v-model="lastName" label="Last Name"/>
                </Column>
            </Row>
            <FormInput v-model="email" label="Email"/>
            <FormInput v-model="phone" label="Phone"/>
        </DashboardEditSection>

        <EntityTagsSection :tags.sync="tags"/>
    </div>
</template>

<script>
import * as api from '@/api';
import { required } from 'vuelidate/lib/validators';
import DashboardEditSection from '@/components/DashboardEditSection';
import AddressForm from '@/components/form/AddressForm';
import WorkOrderSiteSelector from './WorkOrderSiteSelector';
import WorkOrderMaterialList from './WorkOrderMaterialList';
import WorkOrderHourlyRateOptions from './WorkOrderHourlyRateOptions';
import WorkOrderReportTimeOverrides from './WorkOrderReportTimeOverrides';
import Column from '@/components/common/Column.vue';
import Banner from '@/components/common/Banner.vue';
import EntityTagsSection from '@/components/form/EntityTagsSection';

export default {
    name: 'WorkOrderForm',

    components: {
        DashboardEditSection,
        AddressForm,
        WorkOrderSiteSelector,
        WorkOrderMaterialList,
        WorkOrderHourlyRateOptions,
        WorkOrderReportTimeOverrides,
        EntityTagsSection,
    },

    data: () => ({
        isSitesRestricted: false,
        isTimeOverridesEnabled: false,
    }),

    props: {
        isEditMode: { type: Boolean, default: false },
    },

    syncProps: [
        'id',
        'client',
        'name',
        'address',
        'city',
        'state',
        'zip',
        'firstName',
        'lastName',
        'email',
        'phone',
        'rateType',
        'hourlyRate',
        'hourlyRateOptions',
        'reportTimeOverrides',
        'materialRates',
        'mobilizationPrice',
        'demobilizationPrice',
        'loadSite',
        'dumpSite',
        'notes',
        'isSitesEnabled',
        'tags',
        // Relations
        'materials',
        'sites',
    ],

    validations: {
        client: {
            required,
        },
        name: {
            required,
        },
    },

    methods: {
        validate() {
            this.$v.$touch();
            return !this.$v.$invalid;
        },

        async handleToggleSitesEnabled(isEnabled) {
            if (!this.isEditMode) return;

            const res = await api.updateWorkOrder({
                id: this.id,
                isSitesEnabled: isEnabled,
            });
            if (res.error) {
                return this.$awn.alert('Failed to update work order');
            }

            const message = isEnabled ? 'enabled' : 'disabled';
            this.$awn.success(`Sites ${message} for work order`);
        },

        handleToggleRestrictSites(isEnabled) {
            // Clear restricted sites when toggle is off
            if (!isEnabled) {
                this.$refs.workOrderSiteSelector.clearAllSites();
            }
        },

        handleToggleTimeOverrides(isEnabled) {
            // Clear report time overrides when toggle is off
            if (!isEnabled) {
                this.$refs.workOrderReportTimeOverrides.clearAllOverrides();
            }
        },
    },

    mounted() {
        this.isSitesRestricted = Boolean(this.sites?.length);

        const overrides = this.reportTimeOverrides;
        this.isTimeOverridesEnabled = overrides && Object.values(overrides).some(value => value > 0);
    },
}
</script>

<style lang="scss" scoped>
@import 'globals.scss';
.WorkOrderForm {

    .toggleRow {
        margin-bottom: 30px;

        p {
            margin: 5px 0;
            font-size: 14px;
            color: $text-color-muted;
        }
    }

    .sectionHint {
        margin: 0 0 20px;
        font-size: 14px;
        color: $text-color-muted;
    }

    .rateTypeSelector {
        margin-bottom: 0;

        ::v-deep select {
            border-bottom-right-radius: 0;
            border-bottom-left-radius: 0;
        }
    }

    .pricingSection {
        h2 {
            margin-bottom: 5px;
        }

        p {
            margin-bottom: $grid-gutter-width;
        }
    }

    .pricingType {
        border-bottom-left-radius: $border-radius;
        border-bottom-right-radius: $border-radius;
        border: 1px solid $divider-color;
        border-top: none;
        padding: $grid-gutter-width;
        margin-bottom: 30px;
    }

    .Banner {
        margin-bottom: 15px;
    }
}
</style>
